import React from 'react';
import { Helmet } from 'react-helmet-async';

interface AdvancedStructuredDataProps {
  pageType: 'city' | 'country' | 'home' | 'therapist' | 'article';
  entityData?: any;
  businessHours?: string[];
  services?: Array<{
    name: string;
    description: string;
    price?: string;
    duration?: string;
  }>;
  ratings?: {
    averageRating: number;
    reviewCount: number;
    reviews?: Array<{
      author: string;
      rating: number;
      text: string;
      date: string;
    }>;
  };
  contactInfo?: {
    phone?: string;
    email?: string;
    address?: {
      street?: string;
      city?: string;
      country?: string;
      postalCode?: string;
    };
  };
}

const AdvancedStructuredData: React.FC<AdvancedStructuredDataProps> = ({
  pageType,
  entityData,
  businessHours = ['Mo-Su 00:00-23:59'],
  services = [],
  ratings,
  contactInfo
}) => {
  const baseUrl = 'https://psychotherapist.help';

  // Generate comprehensive Medical Business schema for cities
  const generateCityMedicalBusinessSchema = (city: any) => {
    if (!city) return null;
    
    const cityName = city.name?.replace(/\s+\d+$/, '').trim() || 'Unknown City';
    
    return {
      "@context": "https://schema.org",
      "@type": "MedicalBusiness",
      "@id": `${baseUrl}/city/${city.slug}#medicalbusiness`,
      "name": `Professional Mental Health Services in ${cityName}`,
      "alternateName": [`Therapy Services ${cityName}`, `Mental Health ${cityName}`],
      "description": `Comprehensive mental health and therapy services in ${cityName}, ${city.country}. Licensed psychotherapists, psychologists, and counselors providing professional treatment for anxiety, depression, trauma, and relationship issues.`,
      "url": `${baseUrl}/city/${city.slug}`,
      "image": [`${baseUrl}/images/therapy-${city.slug}.webp`, `${baseUrl}/images/mental-health-${cityName.toLowerCase()}.webp`],
      "logo": {
        "@type": "ImageObject",
        "url": `${baseUrl}/logo.png`,
        "width": 200,
        "height": 60
      },
      "address": {
        "@type": "PostalAddress",
        "addressLocality": cityName,
        "addressRegion": city.region || city.country,
        "addressCountry": city.country,
        ...(contactInfo?.address && {
          "streetAddress": contactInfo.address.street,
          "postalCode": contactInfo.address.postalCode
        })
      },
      ...(city.latitude && city.longitude && {
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": city.latitude,
          "longitude": city.longitude
        }
      }),
      "telephone": contactInfo?.phone || "+1-800-THERAPY",
      "email": contactInfo?.email || "support@psychotherapist.help",
      "openingHours": businessHours,
      "priceRange": "$$-$$$",
      "paymentAccepted": ["Cash", "Credit Card", "Debit Card", "Insurance", "PayPal"],
      "currenciesAccepted": ["USD", "EUR", "GBP", "CAD", "AUD"],
      "medicalSpecialty": [
        "Psychology",
        "Psychiatry",
        "Psychotherapy",
        "Clinical Psychology",
        "Counseling Psychology",
        "Marriage and Family Therapy",
        "Substance Abuse Counseling",
        "Trauma Therapy"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": `Mental Health Services in ${cityName}`,
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "MedicalTherapy",
              "name": "Individual Psychotherapy",
              "description": "One-on-one therapy sessions for anxiety, depression, trauma, and personal development",
              "serviceType": "Mental Health Treatment"
            },
            "price": "80-150",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock"
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "MedicalTherapy",
              "name": "Couples Therapy",
              "description": "Relationship counseling and couples therapy for communication and conflict resolution",
              "serviceType": "Relationship Counseling"
            },
            "price": "120-200",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock"
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "MedicalTherapy",
              "name": "Online Therapy",
              "description": "Secure video therapy sessions with licensed mental health professionals",
              "serviceType": "Teletherapy"
            },
            "price": "60-120",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock"
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "MedicalTherapy",
              "name": "Family Therapy",
              "description": "Family counseling sessions to improve family dynamics and communication",
              "serviceType": "Family Counseling"
            },
            "price": "100-180",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock"
          }
        ]
      },
      "serviceArea": {
        "@type": "City",
        "name": cityName,
        "containedInPlace": {
          "@type": "Country",
          "name": city.country
        }
      },
      ...(ratings && {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": ratings.averageRating,
          "reviewCount": ratings.reviewCount,
          "bestRating": 5,
          "worstRating": 1
        }
      }),
      ...(ratings?.reviews && {
        "review": ratings.reviews.map(review => ({
          "@type": "Review",
          "author": {
            "@type": "Person",
            "name": review.author
          },
          "datePublished": review.date,
          "reviewBody": review.text,
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": review.rating,
            "bestRating": 5
          }
        }))
      }),
      "foundingDate": "2024",
      "founder": {
        "@type": "Person",
        "name": "Dr. José Luis Marín López",
        "jobTitle": "Psychiatrist and Psychotherapist",
        "description": "Renowned mental health professional with over 40 years of experience"
      },
      "parentOrganization": {
        "@type": "Organization",
        "name": "Global Therapy Connect",
        "url": baseUrl
      },
      "sameAs": [
        "https://www.facebook.com/globaltherapyconnect",
        "https://www.instagram.com/globaltherapyconnect",
        "https://www.linkedin.com/company/global-therapy-connect"
      ]
    };
  };

  // Generate Service schema for countries
  const generateCountryServiceSchema = (country: any) => {
    if (!country) return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${baseUrl}/psychologists/${country.slug}#service`,
      "name": `Mental Health Services in ${country.name}`,
      "alternateName": [`Therapy Services ${country.name}`, `Psychological Services ${country.name}`],
      "description": `Comprehensive mental health and therapy services throughout ${country.name}. Professional network of licensed psychotherapists, psychologists, and mental health counselors.`,
      "url": `${baseUrl}/psychologists/${country.slug}`,
      "image": `${baseUrl}/images/therapy-${country.slug}.webp`,
      "provider": {
        "@type": "Organization",
        "name": "Global Therapy Connect",
        "url": baseUrl,
        "logo": `${baseUrl}/logo.png`
      },
      "serviceType": "Mental Health Services",
      "category": ["Healthcare", "Mental Health", "Therapy", "Counseling"],
      "areaServed": {
        "@type": "Country",
        "name": country.name,
        "alternateName": country.alternateName || []
      },
      "availableChannel": [
        {
          "@type": "ServiceChannel",
          "serviceUrl": `${baseUrl}/psychologists/${country.slug}`,
          "serviceType": "Online Platform",
          "availableLanguage": ["en", "es", "fr", "de", "it", "pt"]
        },
        {
          "@type": "ServiceChannel",
          "servicePhone": "+1-800-THERAPY",
          "serviceType": "Phone Support",
          "hoursAvailable": "Mo-Su 00:00-23:59"
        }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": `Therapy Services in ${country.name}`,
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Individual Therapy",
              "serviceType": "Mental Health Counseling",
              "description": "Personal therapy sessions with licensed professionals"
            },
            "availability": "https://schema.org/InStock"
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Couples Therapy",
              "serviceType": "Relationship Counseling",
              "description": "Professional couples and relationship counseling"
            },
            "availability": "https://schema.org/InStock"
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Online Therapy",
              "serviceType": "Teletherapy",
              "description": "Secure video therapy sessions"
            },
            "availability": "https://schema.org/InStock"
          }
        ]
      },
      "audience": {
        "@type": "Audience",
        "geographicArea": {
          "@type": "Country",
          "name": country.name
        }
      },
      ...(ratings && {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": ratings.averageRating,
          "reviewCount": ratings.reviewCount,
          "bestRating": 5
        }
      })
    };
  };

  // Generate Organization schema for home page
  const generateOrganizationSchema = () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${baseUrl}#organization`,
    "name": "Global Therapy Connect",
    "alternateName": ["Global Therapy Hub", "GTC"],
    "url": baseUrl,
    "logo": {
      "@type": "ImageObject",
      "url": `${baseUrl}/logo.png`,
      "width": 200,
      "height": 60
    },
    "image": [`${baseUrl}/images/global-therapy-platform.webp`, `${baseUrl}/images/mental-health-worldwide.webp`],
    "description": "Global platform connecting patients with verified mental health professionals worldwide for online and in-person therapy sessions",
    "slogan": "Professional Mental Health Support Worldwide",
    "foundingDate": "2024",
    "foundingLocation": {
      "@type": "Place",
      "name": "Global"
    },
    "founder": {
      "@type": "Person",
      "name": "Dr. José Luis Marín López",
      "jobTitle": "Chief Training Officer & Founder",
      "description": "Renowned psychiatrist and psychotherapist with over 40 years of experience in mental health",
      "alumniOf": {
        "@type": "EducationalOrganization",
        "name": "Complutense University of Madrid"
      },
      "award": ["President Spanish Society of Psychosomatic Medicine", "Founder International Forum for Psychotherapy Training"]
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+1-800-THERAPY",
        "contactType": "Customer Service",
        "availableLanguage": ["English", "Spanish", "French", "German", "Italian", "Portuguese"],
        "hoursAvailable": "Mo-Su 00:00-23:59",
        "contactOption": "TollFree"
      },
      {
        "@type": "ContactPoint",
        "email": "support@psychotherapist.help",
        "contactType": "Customer Support",
        "availableLanguage": ["English", "Spanish", "French", "German", "Italian", "Portuguese"]
      }
    ],
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "Global",
      "addressLocality": "Worldwide"
    },
    "sameAs": [
      "https://www.facebook.com/globaltherapyconnect",
      "https://www.twitter.com/globaltherapy",
      "https://www.instagram.com/globaltherapyconnect",
      "https://www.linkedin.com/company/global-therapy-connect",
      "https://www.youtube.com/c/globaltherapyconnect"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Global Mental Health Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Global Therapist Directory",
            "serviceType": "Mental Health Platform",
            "description": "Access to verified mental health professionals worldwide"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Online Therapy Platform",
            "serviceType": "Teletherapy",
            "description": "Secure video therapy sessions with licensed professionals"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Professional Training",
            "serviceType": "Education",
            "description": "Master's Degree in Brief Psychotherapy training program"
          }
        }
      ]
    },
    "knowsAbout": [
      "Mental Health",
      "Psychotherapy",
      "Psychology",
      "Counseling",
      "Anxiety Treatment",
      "Depression Treatment",
      "Couples Therapy",
      "Family Therapy",
      "Online Therapy",
      "Teletherapy"
    ],
    "memberOf": {
      "@type": "Organization",
      "name": "International Mental Health Organizations"
    },
    ...(ratings && {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": ratings.averageRating,
        "reviewCount": ratings.reviewCount,
        "bestRating": 5,
        "worstRating": 1
      }
    })
  });

  // Generate WebSite schema with enhanced search functionality
  const generateWebsiteSchema = () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${baseUrl}#website`,
    "name": "Global Therapy Connect",
    "alternateName": "Global Therapy Hub",
    "url": baseUrl,
    "description": "Find verified mental health professionals worldwide. Secure online and in-person therapy sessions.",
    "inLanguage": ["en", "es", "fr", "de", "it", "pt"],
    "potentialAction": [
      {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${baseUrl}/find?q={search_term_string}&location={location_string}&specialty={specialty_string}`,
          "actionPlatform": ["http://schema.org/DesktopWebPlatform", "http://schema.org/MobileWebPlatform"]
        },
        "query-input": [
          "required name=search_term_string",
          "optional name=location_string", 
          "optional name=specialty_string"
        ]
      }
    ],
    "publisher": {
      "@type": "Organization",
      "name": "Global Therapy Connect",
      "logo": {
        "@type": "ImageObject",
        "url": `${baseUrl}/logo.png`
      }
    }
  });

  // Select appropriate schemas based on page type
  const getSchemas = () => {
    const schemas = [];
    
    // Always include website schema
    schemas.push(generateWebsiteSchema());
    
    switch (pageType) {
      case 'city':
        const medicalBusiness = generateCityMedicalBusinessSchema(entityData);
        if (medicalBusiness) schemas.push(medicalBusiness);
        break;
        
      case 'country':
        const service = generateCountryServiceSchema(entityData);
        if (service) schemas.push(service);
        break;
        
      case 'home':
        schemas.push(generateOrganizationSchema());
        break;
    }
    
    return schemas;
  };

  const schemas = getSchemas();

  return (
    <Helmet>
      {schemas.map((schema, index) => (
        <script key={`advanced-schema-${index}`} type="application/ld+json">
          {JSON.stringify(schema, null, 0)}
        </script>
      ))}
    </Helmet>
  );
};

export default AdvancedStructuredData;